import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { hasModuleAccess, clearModuleCache, patchModuleCache } from '@/lib/hasModuleAccess';
import { supabase } from '@/lib/supabaseClient';
import { getTenantId } from '@/lib/getTenant';
import Swal from 'sweetalert2';

/* ─── Types ─────────────────────────────────────────────────── */
type ReqStatus = 'pending' | 'approved' | 'rejected' | 'cancelled' | null;

interface Props {
  moduleId: string;
  moduleName?: string;
  children: ReactNode;
}

/* ─── Module metadata ─────────────────────────────────────────── */
const MODULE_NAMES: Record<string, string> = {
  digital_invitations:    'Digital Invitations',
  media_packages:         'Media Packages',
  dvd_online:             'DVD / Online Album',
  ai_sales_assistant:     'AI Sales Assistant',
  appointments:           'Appointments',
  whatsapp_notifications: 'WhatsApp Notifications',
  email_automations:      'Email Automations',
  custom_domain:          'Custom Domain',
  photo_selection:        'Photo Selection',
  final_deliveries:       'Final Deliveries',
  signature_book:         'Signature Book',
};

const MODULE_DESC: Record<string, string> = {
  digital_invitations:    'Create and send digital invitations for your clients\' events.',
  media_packages:         'Sell photo & video packages and manage deliveries.',
  dvd_online:             'Deliver final albums online or as digital downloads.',
  ai_sales_assistant:     'AI-powered chatbot for automated lead capture and replies.',
  appointments:           'Calendar booking and appointment scheduling for clients.',
  whatsapp_notifications: 'Automated WhatsApp messages for reminders and updates.',
  email_automations:      'Email flows for follow-ups, campaigns and automations.',
  custom_domain:          'Connect a custom domain to your public website.',
  photo_selection:        'Client photo selection and gallery management for jobs.',
  final_deliveries:       'Final file delivery portal — galleries, videos, ZIPs and more.',
  signature_book:         'Digital guest book with photos, messages and signatures for your clients\' events. Sell or gift it per contract.',
};

const MODULE_ICON: Record<string, string> = {
  digital_invitations:    'ri-mail-send-line',
  media_packages:         'ri-folder-image-line',
  dvd_online:             'ri-disc-line',
  ai_sales_assistant:     'ri-robot-line',
  appointments:           'ri-calendar-line',
  whatsapp_notifications: 'ri-whatsapp-line',
  email_automations:      'ri-mail-settings-line',
  custom_domain:          'ri-earth-line',
  photo_selection:        'ri-image-2-line',
  final_deliveries:       'ri-send-plane-line',
  signature_book:         'ri-book-open-line',
};

/* ─── Component ──────────────────────────────────────────────── */
export default function ModuleGate({ moduleId, moduleName, children }: Props) {
  const [checking, setChecking]         = useState(true);
  const [hasAccess, setHasAccess]       = useState(false);
  const [reqStatus, setReqStatus]       = useState<ReqStatus>(null);
  const [reqId, setReqId]               = useState<string | null>(null);
  const [reqMessage, setReqMessage]     = useState<string | null>(null);
  const [tenantId, setTenantId]         = useState<string | null>(null);
  const [submitting, setSubmitting]     = useState(false);
  const [modulePrice, setModulePrice]   = useState<number | null>(null);

  const recheck = useCallback(async (clearCache = false) => {
    if (clearCache) clearModuleCache();
    const [access, tid] = await Promise.all([
      hasModuleAccess(moduleId),
      getTenantId(),
    ]);
    setHasAccess(access);
    setTenantId(tid);

    if (!access && tid) {
      const [reqRes, modRes] = await Promise.all([
        supabase
          .from('tenant_module_requests')
          .select('id, status, message')
          .eq('tenant_id', tid)
          .eq('module_id', moduleId)
          .not('status', 'eq', 'cancelled')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('saas_modules')
          .select('price_monthly')
          .eq('id', moduleId)
          .maybeSingle(),
      ]);
      if (reqRes.data) {
        setReqStatus(reqRes.data.status as ReqStatus);
        setReqId(reqRes.data.id);
        setReqMessage(reqRes.data.message ?? null);
      } else {
        setReqStatus(null); setReqId(null); setReqMessage(null);
      }
      if (modRes.data) {
        setModulePrice((modRes.data as { price_monthly: number }).price_monthly ?? null);
      }
    }
    setChecking(false);
  }, [moduleId]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      // Fresh check — respects TTL cache
      const [access, tid] = await Promise.all([
        hasModuleAccess(moduleId),
        getTenantId(),
      ]);
      if (cancelled) return;
      setHasAccess(access);
      setTenantId(tid);

      if (!access && tid) {
        const [reqRes, modRes] = await Promise.all([
          supabase
            .from('tenant_module_requests')
            .select('id, status, message')
            .eq('tenant_id', tid)
            .eq('module_id', moduleId)
            .not('status', 'eq', 'cancelled')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from('saas_modules')
            .select('price_monthly')
            .eq('id', moduleId)
            .maybeSingle(),
        ]);

        if (!cancelled) {
          if (reqRes.data) {
            setReqStatus(reqRes.data.status as ReqStatus);
            setReqId(reqRes.data.id);
            setReqMessage(reqRes.data.message ?? null);
          }
          if (modRes.data) {
            setModulePrice((modRes.data as { price_monthly: number }).price_monthly ?? null);
          }
        }
      }
      if (!cancelled) setChecking(false);
    };
    init();
    return () => { cancelled = true; };
  }, [moduleId]);  // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Checking ─────────────────────────────────────────────── */
  if (checking) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-6 h-6 border-2 border-[#C8A96E]/30 border-t-[#C8A96E] rounded-full animate-spin"></div>
      </div>
    );
  }

  /* ── Access granted ───────────────────────────────────────── */
  if (hasAccess) return <>{children}</>;

  /* ── Locked — shared visuals ──────────────────────────────── */
  const displayName = moduleName ?? MODULE_NAMES[moduleId] ?? moduleId;
  const desc        = MODULE_DESC[moduleId] ?? 'This feature requires an add-on or plan upgrade.';
  const icon        = MODULE_ICON[moduleId] ?? 'ri-lock-line';

  /* ── Handlers ─────────────────────────────────────────────── */
  const handleRequest = async () => {
    if (!tenantId) return;

    const priceHint = modulePrice != null && modulePrice > 0
      ? `<p style="color:rgba(200,169,110,0.7);font-size:12px;margin-bottom:12px;text-align:left;padding:8px 10px;background:rgba(200,169,110,0.08);border:1px solid rgba(200,169,110,0.2);border-radius:8px">
          <strong style="color:#C8A96E">Est. $${modulePrice}/month</strong> — final price set by Super Admin.
        </p>`
      : '';

    const { value, isConfirmed } = await Swal.fire({
      title: `Request ${displayName}`,
      html: `
        <p style="color:rgba(255,255,255,0.45);font-size:13px;margin-bottom:12px;text-align:left">
          Send an activation request to your Super Admin. You can optionally describe your use case.
        </p>
        ${priceHint}
      `,
      input: 'textarea',
      inputLabel: 'Message (optional)',
      inputPlaceholder: 'Describe your use case or expected volume...',
      inputAttributes: { maxlength: '500', style: 'font-size:13px;height:90px' },
      showCancelButton: true,
      confirmButtonText: 'Send Request',
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      color: '#0f172a',
      confirmButtonColor: '#C8A96E',
      cancelButtonColor: '#374151',
    });

    if (!isConfirmed) return;

    setSubmitting(true);
    const { data, error } = await supabase
      .from('tenant_module_requests')
      .insert({
        tenant_id:     tenantId,
        module_id:     moduleId,
        message:       value || null,
        status:        'pending',
        // Populate price_monthly from saas_modules so SA sees the default price
        price_monthly: modulePrice ?? null,
      })
      .select('id, status, message')
      .maybeSingle();

    if (error) {
      Swal.fire({ title: 'Error', text: error.message, icon: 'error', background: '#ffffff', color: '#0f172a' });
    } else if (data) {
      setReqStatus('pending');
      setReqId(data.id);
      setReqMessage(data.message ?? null);
      Swal.fire({
        title: 'Request sent!',
        text: 'Your Super Admin will review it shortly.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        background: '#ffffff',
        color: '#0f172a',
      });
    }
    setSubmitting(false);
  };

  const handleCancel = async () => {
    if (!reqId || !tenantId) return;
    const result = await Swal.fire({
      title: 'Cancel request?',
      text: 'Your activation request will be withdrawn.',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel it',
      cancelButtonText: 'Keep',
      background: '#ffffff',
      color: '#0f172a',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#374151',
    });
    if (!result.isConfirmed) return;
    setSubmitting(true);
    await supabase
      .from('tenant_module_requests')
      .update({ status: 'cancelled', updated_at: new Date().toISOString() })
      .eq('id', reqId).eq('tenant_id', tenantId);
    setReqStatus(null);
    setReqId(null);
    setReqMessage(null);
    setSubmitting(false);
  };

  const handleRequestAgain = async () => {
    setReqStatus(null);
    setReqId(null);
    setReqMessage(null);
    await handleRequest();
  };

  /* ── Pending state ────────────────────────────────────────── */
  if (reqStatus === 'pending') {
    return (
      <div className="flex items-center justify-center py-20 px-6">
        <div className="max-w-sm w-full text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-amber-50 border border-amber-200">
              <i className={`${icon} text-3xl text-amber-400/40`}></i>
            </div>
            <div className="absolute -bottom-1.5 -right-1.5 w-7 h-7 flex items-center justify-center rounded-full bg-amber-400/20 border border-amber-400/40">
              <i className="ri-time-line text-xs text-amber-400"></i>
            </div>
          </div>
          <h2 className="text-[#0f172a] text-lg font-semibold mb-1">Request pending</h2>
          <p className="text-amber-400/80 text-sm font-medium mb-2">{displayName}</p>
          <p className="text-slate-500 text-sm leading-relaxed mb-8">
            Your activation request is under review by your Super Admin. You'll get access once it's approved.
          </p>
          {reqMessage && (
            <div className="mb-6 text-left px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl">
              <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Your message</p>
              <p className="text-slate-500 text-xs leading-relaxed">{reqMessage}</p>
            </div>
          )}
          <button
            onClick={handleCancel}
            disabled={submitting}
            className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-slate-50 hover:bg-red-50 hover:text-red-500 text-slate-500 text-sm font-medium rounded-lg border border-slate-200 hover:border-red-200 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {submitting ? (
              <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <i className="ri-close-line"></i>
            )}
            Cancel Request
          </button>
        </div>
      </div>
    );
  }

  /* ── Approved (module check still false — SA just approved, cache may be stale) ── */
  if (reqStatus === 'approved') {
    const handleActivateNow = async () => {
      setChecking(true);
      // Patch local cache optimistically, then force recheck
      await patchModuleCache(moduleId, true);
      await recheck(true); // full cache-busted recheck
    };
    return (
      <div className="flex items-center justify-center py-20 px-6">
        <div className="max-w-sm w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-emerald-50 border border-emerald-200">
            <i className="ri-checkbox-circle-line text-4xl text-emerald-500/70"></i>
          </div>
          <h2 className="text-[#0f172a] text-lg font-semibold mb-1">Request approved!</h2>
          <p className="text-emerald-600 text-sm font-medium mb-2">{displayName}</p>
          <p className="text-slate-500 text-sm leading-relaxed">
            Your Super Admin approved this module. Click below to activate it now.
          </p>
          <button
            onClick={handleActivateNow}
            className="mt-6 flex items-center gap-2 mx-auto px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Activate Now
          </button>
          <p className="text-slate-300 text-xs mt-3">This will reload your access without a full page refresh.</p>
        </div>
      </div>
    );
  }

  /* ── Rejected state ───────────────────────────────────────── */
  if (reqStatus === 'rejected') {
    return (
      <div className="flex items-center justify-center py-20 px-6">
        <div className="max-w-sm w-full text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-red-50 border border-red-200">
              <i className={`${icon} text-3xl text-red-400/40`}></i>
            </div>
            <div className="absolute -bottom-1.5 -right-1.5 w-7 h-7 flex items-center justify-center rounded-full bg-red-400/20 border border-red-400/30">
              <i className="ri-close-line text-xs text-red-400"></i>
            </div>
          </div>
          <h2 className="text-[#0f172a] text-lg font-semibold mb-1">Request rejected</h2>
          <p className="text-red-400/80 text-sm font-medium mb-2">{displayName}</p>
          {reqMessage && (
            <div className="mb-6 text-left px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Admin note</p>
              <p className="text-slate-500 text-xs leading-relaxed">{reqMessage}</p>
            </div>
          )}
          <p className="text-slate-500 text-sm leading-relaxed mb-8">
            Your request was not approved. You can submit a new request if needed.
          </p>
          <button
            onClick={handleRequestAgain}
            disabled={submitting}
            className="flex items-center gap-2 mx-auto px-6 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {submitting ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <i className="ri-send-plane-line"></i>
            )}
            Request Again
          </button>
        </div>
      </div>
    );
  }

  /* ── Default: no request yet ──────────────────────────────── */
  return (
    <div className="flex items-center justify-center py-20 px-6">
      <div className="max-w-sm w-full text-center">
        <div className="relative w-20 h-20 mx-auto mb-6">
          <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-slate-100 border border-slate-200">
            <i className={`${icon} text-3xl text-slate-300`}></i>
          </div>
          <div className="absolute -bottom-1.5 -right-1.5 w-7 h-7 flex items-center justify-center rounded-full bg-blue-50 border border-blue-200">
            <i className="ri-lock-line text-xs text-[#C8A96E]"></i>
          </div>
        </div>

        <h2 className="text-[#0f172a] text-lg font-semibold mb-2">Module not active</h2>
        <p className="text-[#C8A96E] text-sm font-medium mb-1">{displayName}</p>
        <p className="text-slate-500 text-sm leading-relaxed mb-5">{desc}</p>

        {/* Price badge */}
        {modulePrice != null && modulePrice > 0 && (
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C8A96E]/10 border border-[#C8A96E]/25 text-[#C8A96E] text-xs font-medium">
              <i className="ri-price-tag-3-line text-xs"></i>
              Add-on — from ${modulePrice}<span className="text-[#C8A96E]/60">/month</span>
            </span>
          </div>
        )}
        {modulePrice === 0 && (
          <div className="flex items-center justify-center mb-6">
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/25 text-emerald-400 text-xs font-medium">
              <i className="ri-gift-line text-xs"></i>
              Free add-on
            </span>
          </div>
        )}

        <button
          onClick={handleRequest}
          disabled={submitting}
          className="flex items-center gap-2 mx-auto px-6 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {submitting ? (
            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
          ) : (
            <i className="ri-send-plane-line"></i>
          )}
          Request Activation
        </button>
        <p className="text-slate-300 text-xs mt-5 leading-relaxed">
          Your Super Admin will review and activate this add-on.
        </p>
      </div>
    </div>
  );
}
